// Validate's durable + view models (Slice 5; slice-plan.md:70-81; D77 hardened upload, D32
// brand+mockups, D35 guided workflow validation).
//
// Validate turns a vague idea into a research-GROUNDED, CITED verdict pack, then a brand + mockups the
// founder approves before any Build. Two load-bearing invariants are STRUCTURAL here, mirroring
// certify.ts's D46 self-assessment enforcement (model/certify.ts):
//   - THE ANTI-HALLUCINATION WALL (§0 #4; slice-plan.md:75). A claim that STATES A FIGURE (a
//     `modeled_estimate` or a `sourced` value) with ZERO evidence is UNPARSEABLE (VerdictClaim's
//     .refine). "asserted"/"invented" is NOT a FigureKind, so a figure with no grounding cannot even
//     be labeled. This is the AskCrewAnswer cited-or-abstain shape (ports/knowledge.ts:123) applied to
//     the verdict pack: a `passed` verdict (which advances the app to Validated, D34) is unrepresentable
//     unless every section carries at least one cited claim.
//   - THE HARDENED-UPLOAD WALL (§0 #3; D77; slice-plan.md:76). A REJECTED upload STRUCTURALLY cannot
//     carry a `fileRef`/`extractedTextRef` (the `rejected` union member has no such field), so the crew
//     cannot read a blocked file - "blocked but readable" is not a value.
//
// BOUNDARY: no figure, legal, or compliance VALUE is authored here (§8). These are type contracts only;
// the verdict's numbers come from the ResearchClient (cited) at runtime, never invented in the schema.
import { z } from "zod";
import { NonEmptyString, Timestamp } from "./common.js";
import { EvidencePointer } from "./certify.js";
// --- The research-grounded verdict pack (§0 #4; the AskCrewAnswer cited-or-abstain shape) ---
// BOUNDARY (mirrors model/certify.ts:14-16). The anti-hallucination refine below is a TYPE wall over
// the LABELED path ONLY: it forces a claim TAGGED `modeled_estimate`/`sourced` to carry evidence. It
// CANNOT catch a figure smuggled into `VerdictSection.summary` free text, nor a numeric claim
// mislabeled `qualitative` - a schema cannot read prose intent. That grounding (scan the free text for
// uncited figures; reject a mislabeled claim) is enforced at VERDICT ASSEMBLY in packages/validate
// (Wave 2), where the research output is turned into claims. Do not try to close it in the type.
/**
 * How a claim's figure is grounded. A CLOSED set that DELIBERATELY OMITS "asserted"/"invented": a
 * figure is either a `modeled_estimate` (derived, its model cited), `sourced` (from a cited external
 * source), or `qualitative` (a non-numeric statement). There is no value for an ungrounded number, so
 * a hallucinated figure cannot even be labeled (§0 #4; slice-plan.md:75).
 */
export const FigureKind = z.enum(["modeled_estimate", "sourced", "qualitative"]);
/**
 * One claim in the verdict. THE ANTI-HALLUCINATION WALL is structural: a claim that STATES A FIGURE
 * (`modeled_estimate` | `sourced`) with an EMPTY `evidence` array is UNPARSEABLE (the .refine). Only a
 * `qualitative` claim may carry zero evidence. `evidence` reuses certify's `EvidencePointer`
 * (model/certify.ts:61) - the same dated, PII-free grounding pointer, never redefined.
 *
 * BOUNDARY: the wall covers the LABELED `figureKind` path only. A figure hidden in a claim mislabeled
 * `qualitative`, or in `VerdictSection.summary` free text, is NOT caught here - that is enforced at
 * verdict assembly in packages/validate (Wave 2). See the boundary note above `FigureKind`.
 */
export const VerdictClaim = z
    .object({
    statement: NonEmptyString,
    figureKind: FigureKind,
    evidence: z.array(EvidencePointer),
})
    .refine((c) => c.figureKind === "qualitative" || c.evidence.length >= 1, {
    message: "a claim stating a figure (modeled_estimate|sourced) must carry at least one evidence pointer (anti-hallucination, §0 #4)",
    path: ["evidence"],
});
/** The four verdict sections the research pack grounds (slice-plan.md:73-75; workflow 2). */
export const VerdictSectionKind = z.enum([
    "market_size",
    "competitors",
    "demand",
    "financial_plan",
]);
/**
 * One section of the verdict: its grounded claims plus a plain-language summary. BOUNDARY: `summary`
 * is free text - the type CANNOT verify a figure inside it is grounded; verdict assembly in
 * packages/validate (Wave 2) scans the summary for uncited figures (see the `FigureKind` boundary note).
 */
export const VerdictSection = z.object({
    kind: VerdictSectionKind,
    claims: z.array(VerdictClaim),
    summary: NonEmptyString,
});
/** The overall verdict score. `passed` -> the app may advance to Validated (D34). */
export const VerdictScore = z.enum(["passed", "conditional", "low"]);
/**
 * The scored, sectioned verdict member. STRUCTURAL: `isAdvisory` is z.literal(true) - a verdict can
 * NEVER present as a guarantee (§0 #4). And a `passed` verdict (which advances the app to Validated,
 * D34) is UNREPRESENTABLE unless it has AT LEAST ONE section AND every section carries at least one
 * cited claim (the .refine), so an idea cannot be "validated" on ungrounded OR ZERO claims. The
 * `sections.length >= 1` guard closes the vacuous-truth hole: `[].every()` is true, so without it a
 * `passed` verdict with NO sections would validate an app on nothing (cited-or-abstain, ports/knowledge.ts:131).
 */
const VerdictMember = z
    .strictObject({
    kind: z.literal("verdict"),
    score: VerdictScore,
    sections: z.array(VerdictSection),
    isAdvisory: z.literal(true),
    assessedAt: Timestamp,
})
    .refine((v) => v.score !== "passed" ||
    (v.sections.length >= 1 && v.sections.every((s) => s.claims.some((c) => c.evidence.length >= 1))), {
    message: "a `passed` verdict (-> Validated) requires at least one section, each with at least one cited claim (cited-or-abstain, §0 #4)",
    path: ["sections"],
});
/**
 * The verdict pack, a cited-or-abstain discriminated union (the AskCrewAnswer shape,
 * ports/knowledge.ts:123). `verdict` carries a scored, sectioned, ADVISORY result; `insufficient_evidence`
 * ABSTAINS, naming the gaps (at least one). The two members are unswappable; an abstain carries no
 * score and no sections, so a "verdict" cannot pose as an abstain or vice versa.
 */
export const VerdictPack = z.discriminatedUnion("kind", [
    VerdictMember,
    z.strictObject({
        kind: z.literal("insufficient_evidence"),
        gaps: z.array(NonEmptyString).min(1),
    }),
]);
// --- The hardened Validate context upload (§0 #3; D77; slice-plan.md:76) ---
/**
 * The scanner's verdict on an uploaded file. `clean` is the ONLY value that lets the crew read it;
 * every `blocked_*` value denies. `blocked_structure` = the container/structure failed (e.g. a zip
 * bomb or malformed archive); `blocked_content` = an injection/abuse content match (D77 injection wall).
 */
export const UploadScanVerdict = z.enum([
    "clean",
    "blocked_malware",
    "blocked_structure",
    "blocked_content",
]);
/** Where a rejected upload is routed. `route_out` sends an abusive upload out of the normal path (D77). */
export const AbuseRouting = z.enum(["allow", "route_out"]);
/** The upload request metadata. The bytes arrive multipart at the edge; this is the declared envelope. */
export const ValidateUploadRequest = z.object({
    filename: NonEmptyString,
    mime: NonEmptyString,
    declaredSize: z.int().nonnegative(),
});
/**
 * THE HARDENED-UPLOAD WALL (§0 #3; D77). A discriminated union on `state`: an `accepted` upload carries
 * a PRIVATE `fileRef` + `extractedTextRef` and a scan that is LITERALLY `clean`; a `rejected` upload
 * carries the blocking `scan` verdict and the `abuse` routing and STRUCTURALLY has NO
 * fileRef/extractedTextRef. The crew cannot read a blocked file because the `rejected` member has no
 * readable handle to hand it - "blocked but readable" is unrepresentable. `strictObject` makes a
 * rejected result that smuggles a fileRef a PARSE ERROR, not a silently-stripped field.
 * `fileRef`/`extractedTextRef` are private object-storage handles (app-security §1), never public URLs.
 *
 * COHERENCE (Lens-A b): a `rejected` result must have a CAUSE. A `clean`-scanned file may be rejected
 * ONLY when abuse-routed (`abuse === "route_out"`) - a `{scan:"clean", abuse:"allow"}` rejection has no
 * cause and is UNPARSEABLE. A blocked scan may reject with any abuse routing.
 */
export const ValidateUploadResult = z
    .discriminatedUnion("state", [
    z.strictObject({
        state: z.literal("accepted"),
        fileRef: NonEmptyString,
        extractedTextRef: NonEmptyString,
        scan: z.literal("clean"),
    }),
    z.strictObject({
        state: z.literal("rejected"),
        scan: UploadScanVerdict,
        abuse: AbuseRouting,
    }),
])
    .refine((v) => v.state !== "rejected" || v.scan !== "clean" || v.abuse === "route_out", {
    message: "a clean-scanned file may be rejected only when abuse-routed (route_out)",
    path: ["abuse"],
});
// --- Brand direction + mockups (D32; slice-plan.md:77) ---
/**
 * A private media handle (a brand asset, a mockup screen). A SEMANTIC alias of `NonEmptyString`: it is
 * an internal object-storage handle, NEVER a public URL (app-security §1) - mirrors certify's `fileRef`
 * shape (model/certify.ts:183). Named so a review reads "this is a private ref" at the type site.
 */
export const MediaRef = NonEmptyString;
/** A brand direction the founder picks from (D32). `logoRefs` are private media handles, not URLs. */
export const BrandDirection = z.object({
    id: NonEmptyString,
    name: NonEmptyString,
    palette: z.array(NonEmptyString),
    type: NonEmptyString,
    logoRefs: z.array(MediaRef),
    voice: NonEmptyString,
});
/** The generated mockup screens for a chosen direction (D32). Screens are private media handles. */
export const MockupSet = z.object({
    directionId: NonEmptyString,
    screens: z.array(MediaRef),
});
/**
 * The brand/mockup approval gate (D32; slice-plan.md:77 "Build proceeds only on approval"). A
 * discriminated union on `state`: `pending` (awaiting the founder), `approved` (who + when, recorded
 * for the audit, app-security §6), or `regenerate` (the founder asked for another round). Build reads
 * an `approved` gate; the union makes "approved without an approver" unrepresentable.
 */
export const ApprovalGate = z.discriminatedUnion("state", [
    z.strictObject({ state: z.literal("pending") }),
    z.strictObject({
        state: z.literal("approved"),
        approvedBy: NonEmptyString,
        approvedAt: Timestamp,
    }),
    z.strictObject({ state: z.literal("regenerate") }),
]);
// Guided workflow validation (D35) is a BUILD-PHASE gate, not idea-validation: `WorkflowStep` +
// `WorkflowValidation` live in ./build.ts (moved per the Wave-1 reconciliation, D5).
//# sourceMappingURL=validate.js.map